import React, { useEffect } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useApps } from '@/contexts/AppsContext';
import Sidebar from './Sidebar';
import Header from './Header';

const pageTitles = {
  '/dashboard': 'Dashboard',
  '/apps': 'Apps',
  '/apps/new': 'Create New App',
  '/settings': 'Settings',
  '/settings/profile': 'Profile',
  '/settings/notifications': 'Notifications',
  '/documentation': 'Documentation',
  '/admin': 'Platform Overview',
  '/admin/tenants': 'Tenants',
  '/admin/services': 'Service Catalog',
};

// App-scoped route patterns → titles
const appScopedTitles = [
  [/\/apps\/[^/]+\/mind$/, 'Mind'],
  [/\/apps\/[^/]+\/data$/, 'Graph Explorer'],
  [/\/apps\/[^/]+\/keys/, 'API Keys'],
  [/\/apps\/[^/]+\/billing$/, 'Billing & Usage'],
  [/\/apps\/[^/]+\/ingest$/, 'Ingest Content'],
  [/\/apps\/[^/]+\/schema$/, 'Schema'],
];

export default function DashboardLayout() {
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const { apps, fetchApps, loading: appsLoading } = useApps();
  const location = useLocation();

  // Fetch apps on mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchApps().catch(() => {
        // Handle error silently - could be no apps yet
      });
    }
  }, [isAuthenticated, fetchApps]);

  // Show loading spinner while auth or apps are loading — prevents flicker
  if (authLoading || (isAuthenticated && appsLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const hasApps = apps.length > 0;

  // Redirect new users to onboarding wizard
  // Admins skip this — they don't need apps to manage the platform
  const allowedWithoutApps = ['/apps/new', '/documentation'];
  if (!hasApps && !isAdmin && !allowedWithoutApps.includes(location.pathname)) {
    return <Navigate to="/apps/new" replace />;
  }

  // Get page title from route
  const getTitle = () => {
    if (!hasApps && !isAdmin) return 'Onboarding';

    const path = location.pathname;

    // Check app-scoped routes first
    for (const [pattern, title] of appScopedTitles) {
      if (pattern.test(path)) return title;
    }

    // Global routes
    if (pageTitles[path]) return pageTitles[path];
    if (path.match(/^\/apps\/[^/]+$/)) return 'App Details';
    return 'TruContext';
  };

  return (
    <div className="flex h-screen bg-background">
      {(hasApps || isAdmin) && <Sidebar />}
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header title={getTitle()} />
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
